import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Menu, X, ChevronDown, LogOut, User, Sparkles } from 'lucide-react';

export default function Header({ onAuthClick }) {
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isHome = location.pathname === '/';

  const scrollToSection = (id) => {
    if (!isHome) return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'glass border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
          <Sparkles className="w-5 h-5 text-white group-hover:rotate-12 transition-transform" strokeWidth={1.5} />
          <span className="font-heading text-lg font-medium tracking-tight text-white">Codelves</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {isHome && (
            <>
              <button onClick={() => scrollToSection('features')} className="text-sm font-medium text-neutral-400 hover:text-white transition-colors" data-testid="nav-features">Features</button>
              <button onClick={() => scrollToSection('pricing')} className="text-sm font-medium text-neutral-400 hover:text-white transition-colors" data-testid="nav-pricing">Pricing</button>
              <Link to="/workspace" className="text-sm font-medium text-neutral-400 hover:text-white transition-colors" data-testid="nav-workspace">Workspace</Link>
            </>
          )}
          {!isHome && (
            <Link to="/" className="text-sm font-medium text-neutral-400 hover:text-white transition-colors" data-testid="nav-home">Home</Link>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 text-sm text-neutral-300 hover:text-white transition-colors px-3 py-2 rounded-md border border-white/10 hover:border-white/20"
                data-testid="user-menu-button"
              >
                <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs font-medium">
                  {user.displayName?.[0] || user.email?.[0]?.toUpperCase()}
                </div>
                <span className="max-w-[120px] truncate">{user.displayName || user.email}</span>
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-lg border border-white/10 bg-surface p-1 shadow-2xl" data-testid="user-dropdown">
                  <Link to="/account" onClick={() => setDropdownOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-300 hover:text-white hover:bg-white/5 rounded-md transition-colors" data-testid="nav-account">
                    <User className="w-4 h-4" strokeWidth={1.5} /> Account
                  </Link>
                  <button onClick={() => { logout(); setDropdownOpen(false); }} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-neutral-300 hover:text-white hover:bg-white/5 rounded-md transition-colors" data-testid="logout-button">
                    <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <button onClick={() => onAuthClick('login')} className="text-sm font-medium text-neutral-400 hover:text-white transition-colors px-4 py-2" data-testid="login-button">Sign in</button>
              <button onClick={() => onAuthClick('register')} className="inline-flex items-center justify-center rounded-md bg-white text-black px-5 py-2 text-sm font-medium hover:bg-neutral-200 transition-colors" data-testid="signup-button">Get Started</button>
            </>
          )}
        </div>

        <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden text-white" data-testid="mobile-menu-toggle">
          {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden glass border-t border-white/10 px-6 py-4 space-y-3" data-testid="mobile-menu">
          {isHome && (
            <>
              <button onClick={() => scrollToSection('features')} className="block text-sm text-neutral-400 hover:text-white">Features</button>
              <button onClick={() => scrollToSection('pricing')} className="block text-sm text-neutral-400 hover:text-white">Pricing</button>
            </>
          )}
          {user ? (
            <>
              <Link to="/account" onClick={() => setMenuOpen(false)} className="block text-sm text-neutral-400 hover:text-white">Account</Link>
              <button onClick={() => { logout(); setMenuOpen(false); }} className="block text-sm text-neutral-400 hover:text-white">Sign out</button>
            </>
          ) : (
            <>
              <button onClick={() => { onAuthClick('login'); setMenuOpen(false); }} className="block text-sm text-neutral-400 hover:text-white">Sign in</button>
              <button onClick={() => { onAuthClick('register'); setMenuOpen(false); }} className="block w-full text-center rounded-md bg-white text-black px-4 py-2 text-sm font-medium">Get Started</button>
            </>
          )}
        </div>
      )}
    </header>
  );
}
